import React, { useState } from "react";
import { Card, DatePicker, Select, Button, Row, Col, message, Spin, Table } from "antd";
import { CarOutlined } from "@ant-design/icons";
import moment from "moment";
import adminApi from "../../orders/store/api";
import AdminBreadCrumb from "components/AdminBreadCrumb";
import "./DriverBillingReport.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;

const DriverBillingReport = () => {
  // Set default to current year (Jan 1 - Dec 31)
  const getCurrentYearRange = () => {
    const year = moment().year();
    return [moment(`${year}-01-01`), moment(`${year}-12-31`)];
  };

  const [dateRange, setDateRange] = useState(getCurrentYearRange());
  const [statusFilter, setStatusFilter] = useState("completed");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const breadCrumbs = [
    { text: "BILLING", url: "/admin/billing-dashboard" },
    { text: "REPORTS", url: "/admin/billing/reports" },
    { text: "DRIVER BILLING", url: "/admin/billing/reports/drivers" }
  ];

  const handleRunReport = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.error("Please select a valid date range");
      return;
    }

    setLoading(true);
    try {
      const params = {
        startDate: dateRange[0].format("YYYY-MM-DD"),
        endDate: dateRange[1].format("YYYY-MM-DD"),
        statusFilter: statusFilter
      };

      const response = await adminApi.getDriverBillingReport(params);

      setReportData({
        drivers: response.data || [],
        dateRange: `${dateRange[0].format("MMM D, YYYY")} - ${dateRange[1].format(
          "MMM D, YYYY"
        )}`
      });
    } catch (error) {
      console.error("Error fetching driver billing report:", error);
      message.error("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Driver Name",
      dataIndex: "driverName",
      key: "driverName",
      fixed: "left",
      width: 200,
      sorter: (a, b) => (a.driverName || "").localeCompare(b.driverName || "")
    },
    {
      title: "Total Earnings (Estimate)",
      dataIndex: "totalEstimate",
      key: "totalEstimate",
      align: "right",
      width: 180,
      render: (val) => `$${(val || 0).toFixed(2)}`,
      sorter: (a, b) => (a.totalEstimate || 0) - (b.totalEstimate || 0)
    },
    {
      title: "Total Earnings (Final)",
      dataIndex: "totalFinal",
      key: "totalFinal",
      align: "right",
      width: 180,
      render: (val) => `$${(val || 0).toFixed(2)}`,
      sorter: (a, b) => (a.totalFinal || 0) - (b.totalFinal || 0)
    },
    {
      title: "Est Net (75% of Final)",
      key: "estNet",
      align: "right",
      width: 180,
      render: (_, record) => {
        const estNet = (record.totalFinal || 0) * 0.75;
        return `$${estNet.toFixed(2)}`;
      },
      sorter: (a, b) => {
        const aNet = (a.totalFinal || 0) * 0.75;
        const bNet = (b.totalFinal || 0) * 0.75;
        return aNet - bNet;
      }
    },
    {
      title: "# Routes",
      dataIndex: "routeCount",
      key: "routeCount",
      align: "center",
      width: 120,
      sorter: (a, b) => (a.routeCount || 0) - (b.routeCount || 0)
    }
  ];

  const getTotals = () => {
    if (!reportData || !reportData.drivers || reportData.drivers.length === 0) {
      return { totalEstimate: 0, totalFinal: 0, totalEstNet: 0, totalRoutes: 0 };
    }

    return reportData.drivers.reduce(
      (acc, driver) => ({
        totalEstimate: acc.totalEstimate + (driver.totalEstimate || 0),
        totalFinal: acc.totalFinal + (driver.totalFinal || 0),
        totalEstNet: acc.totalEstNet + (driver.totalFinal || 0) * 0.75,
        totalRoutes: acc.totalRoutes + (driver.routeCount || 0)
      }),
      { totalEstimate: 0, totalFinal: 0, totalEstNet: 0, totalRoutes: 0 }
    );
  };

  const totals = reportData ? getTotals() : null;

  return (
    <div className="driver-billing-report-page admin">
      <div className="common-header">
        <AdminBreadCrumb breadCrumbs={breadCrumbs} />
      </div>

      <div className="content-wrapper">
        <h2>Driver Billing Report</h2>
        <p style={{ marginBottom: 24, color: "#666" }}>
          View driver earnings by estimate and final prices
        </p>

        <Card title="Report Parameters" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Date Range</div>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Route Status</div>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: "100%" }}
                placeholder="Select status filter"
              >
                <Option value="completed">Completed Only</Option>
                <Option value="all">All Statuses</Option>
              </Select>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>&nbsp;</div>
              <Button
                type="primary"
                icon={<CarOutlined />}
                onClick={handleRunReport}
                loading={loading}
                style={{ width: "100%" }}
              >
                Run Report
              </Button>
            </Col>
          </Row>
        </Card>

        {reportData && totals && (
          <Card
            title="Driver Earnings Summary"
            extra={
              <div>
                <span style={{ marginRight: 16 }}>
                  <strong>Total Estimate: </strong>
                  <span style={{ color: "#1890ff", fontSize: 16 }}>
                    ${totals.totalEstimate.toFixed(2)}
                  </span>
                </span>
                <span style={{ marginRight: 16 }}>
                  <strong>Total Final: </strong>
                  <span style={{ color: "#52c41a", fontSize: 16 }}>
                    ${totals.totalFinal.toFixed(2)}
                  </span>
                </span>
                <span style={{ marginRight: 16 }}>
                  <strong>Total Est Net: </strong>
                  <span style={{ color: "#eb2f96", fontSize: 16 }}>
                    ${totals.totalEstNet.toFixed(2)}
                  </span>
                </span>
                <span>
                  <strong>Routes: </strong>
                  {totals.totalRoutes}
                </span>
              </div>
            }
          >
            <Table
              columns={columns}
              dataSource={reportData.drivers}
              rowKey="driverId"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} drivers`
              }}
              scroll={{ x: 900 }}
            />
          </Card>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: "#666" }}>Generating report...</div>
          </div>
        )}

        {!reportData && !loading && (
          <Card>
            <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
              <CarOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <h3>Driver Billing Report</h3>
              <p>Select parameters and click Run Report to view driver earnings data.</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DriverBillingReport;
